import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";

interface DataPoint {
  label: string;
  value: number;
}

interface BarChartProps {
  data: DataPoint[];
  numOfLines?: number;
  width?: number;
  height?: number;
  line?: boolean;
}

const formatNumber = (num: number) => {
  if (num >= 1e9) return (num / 1e9).toFixed(1) + "B";
  if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
  return num;
};

const BarChart: React.FC<BarChartProps> = ({
  data,
  numOfLines = 5,
  width = 500,
  height = 300,
  line = false,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const maxValue = useMemo(() => {
    return Math.max(...data.map((d) => d.value));
  }, [data]);

  const yAxisMaxValue = useMemo(() => {
    return maxValue * 1.25;
  }, [maxValue]);

  const yAxisTicks = useMemo(() => {
    const ticks = [];
    const interval = yAxisMaxValue / numOfLines;
    for (let i = 0; i <= numOfLines; i++) {
      ticks.push(interval * i);
    }
    return ticks;
  }, [yAxisMaxValue, numOfLines]);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);

    svg.selectAll("*").remove();

    const margin = { top: 20, right: 20, bottom: 70, left: 40 };
    const barWidth = width - margin.left - margin.right;
    const barHeight = height - margin.top - margin.bottom;

    const mousemove = (event: MouseEvent, d: DataPoint) => {
      let xPosition = event.offsetX + margin.left - 25;
      const yPosition = event.offsetY + margin.top - 40;
      const tooltipWidth = tooltip.node()?.getBoundingClientRect()?.width;
      const rightOffset = tooltipWidth ? xPosition + tooltipWidth : xPosition;
      if (rightOffset > width) {
        xPosition = Math.floor(
          tooltipWidth ? xPosition - tooltipWidth - 20 : xPosition - 20,
        );
      }

      const text = `${d.label} - ${d.value}`;
      tooltip
        .attr("x", xPosition)
        .attr("y", yPosition)
        .style("opacity", 9)
        .style("height", text.length >= 16 ? 50 : 35);

      tooltipText.html(text);
    };

    const mouseleave = () => {
      tooltip.style("opacity", 0);
    };

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.label))
      .range([0, barWidth])
      .padding(0.1);

    const y = d3.scaleLinear().domain([0, yAxisMaxValue]).range([barHeight, 0]);

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    if (line) {
      yAxisTicks.slice(1).forEach((tick) => {
        chart
          .append("line")
          .attr("class", "dotted-line")
          .attr("x1", 0)
          .attr("y1", y(tick))
          .attr("x2", barWidth)
          .attr("y2", y(tick))
          .style("stroke", "gray")
          .style("stroke-dasharray", "3,3")
          .style("opacity", 1);
      });
    }

    chart
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      //@ts-expect-error not applicable
      .attr("x", (d: any) => x(d.label))
      .attr("y", (d: any) => y(d.value))
      .attr("width", x.bandwidth())
      .attr("height", (d: any) => barHeight - y(d.value))
      .style("fill", "#357AF6")
      .style("opacity", "1")
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave);

    const xAxis = chart
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${barHeight})`)
      .call(d3.axisBottom(x) as any);

    xAxis.selectAll(".tick line").remove();

    const yAxis = chart
      .append("g")
      .attr("class", "y-axis")
      .call(
        d3
          .axisLeft(y)
          .tickValues(yAxisTicks)
          //@ts-expect-error not applicable
          .tickFormat((d: any) => {
            return formatNumber(d);
          })
          .tickSizeOuter(0) as any,
      );

    yAxis.selectAll(".tick line").remove();

    const tooltip = svg
      .append("foreignObject")
      .attr("class", "tooltip")
      .attr("width", 120)
      .attr("height", 20)
      .style("opacity", 0)
      .style("position", "absolute")
      .style("background-color", "white")
      .style("border", "none")
      .style("box-shadow", "0rem 0rem 0.469rem 0rem rgba(0, 0, 0, 0.15)")
      .style("border-radius", "0.313rem")
      .style("padding", "0.625rem")
      .style("font-size", "0.75rem");

    const tooltipText = tooltip
      .append("xhtml:div")
      .style("font-size", "0.75rem")
      .style("color", "black");
  }, [data, height, width, line, yAxisMaxValue, yAxisTicks]);

  return (
    <>
      <svg ref={svgRef} width={width} height={height}></svg>
    </>
  );
};

export { BarChart };
export default BarChart;
